interface Tab {
    toggle: HTMLButtonElement;
    component: HTMLDivElement;
}

let UNIQUE_ID_COUNTER = 0;

function createHomepageTabs(root: HTMLDivElement): void {
    let activeTab: number | undefined = undefined;
    const tabs: Tab[] = Array.from(root.querySelectorAll<HTMLButtonElement>('[data-homepage-tab-head]'))
        .map((head) => {
            if (!head.nextElementSibling) return undefined;
            if (typeof (head.nextElementSibling as HTMLDivElement).dataset?.['homepageTabComponent'] === 'undefined')
                return undefined;
            return { toggle: head, component: head.nextElementSibling as HTMLDivElement };
        })
        .filter((v: Tab | undefined): v is Tab => typeof v !== 'undefined');

    tabs.forEach((tab, index) => {
        const _internalId = UNIQUE_ID_COUNTER++;
        const toggleId = tab.toggle.id || 'homepage-tabs-tab--' + _internalId + '-toggle';
        const componentId = tab.component.id || 'homepage-tabs-tab--' + _internalId;

        tab.toggle.id = toggleId;
        tab.toggle.setAttribute('aria-controls', componentId);
        tab.toggle.setAttribute('aria-disabled', 'false');

        tab.component.id = componentId;
        tab.component.setAttribute('role', 'region');
        tab.component.setAttribute('aria-hidden', 'true');
        tab.component.setAttribute('aria-labelledby', toggleId);

        tab.toggle.addEventListener('click', () => {
            changeActiveTab(index);
            const scrollBy = root.getBoundingClientRect().y - 21 - 79;
            if (scrollBy > 0) window.scrollBy({ behavior: 'smooth', top: scrollBy });
        });

        tab.toggle.addEventListener('keydown', (e) => {
            const moveMap: Record<string, number> = {
                ArrowUp: (tabs.length + index - 1) % tabs.length,
                ArrowDown: (tabs.length + index + 1) % tabs.length,
                Home: 0,
                End: tabs.length - 1,
            };

            if (typeof moveMap[e.key] !== 'undefined') {
                tabs[moveMap[e.key]!]!.toggle.focus();
                e.preventDefault();
            }
        });
    });

    if (tabs.length > 0) {
        changeActiveTab(0);
    }

    function collapseTab(tab: Tab) {
        tab.toggle.classList.remove('expanded');
        tab.toggle.setAttribute('aria-expanded', 'false');
        tab.toggle.setAttribute('aria-disabled', 'false');

        tab.component.classList.remove('expanded');
        tab.component.setAttribute('aria-hidden', 'true');
    }

    function expandTab(tab: Tab) {
        tab.toggle.classList.add('expanded');
        tab.toggle.setAttribute('aria-expanded', 'true');
        tab.toggle.setAttribute('aria-disabled', 'true');

        tab.component.classList.add('expanded');
        tab.component.setAttribute('aria-hidden', 'false');
    }

    function changeActiveTab(active: number) {
        if (active < 0 || active >= tabs.length)
            throw new Error(
                'Invalid index of active tab passed, must be greater then 0 and less then the number of tabs!'
            );

        if (typeof activeTab === 'number') collapseTab(tabs[activeTab]!);

        activeTab = active;
        expandTab(tabs[activeTab]!);
    }
}

window.Drupal.behaviors['homepageTabs'] = {
    attach(context: Document | HTMLElement, settings: DrupalSettings) {
        document.querySelectorAll<HTMLDivElement>('[data-homepage-tabs]').forEach((el) => {
            if (el.dataset['homepageTabs'] === 'initialized') return;
            el.dataset['homepageTabs'] = 'initialized';
            createHomepageTabs(el);
        });
    },
};
